import { Component, OnInit, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import {
	ItTabContainerComponent,
	ItTabItemComponent,
	ItModalComponent,
	ItIconComponent,
	ItInputComponent,
	ItBreadcrumbComponent,
	ItBreadcrumbItemComponent,
	ItButtonDirective,
	ItSelectComponent,
	ItSearchComponent,
	SearchItem,
	ItCheckboxComponent,
} from 'design-angular-kit';
import { TranslateModule } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { DossierComponent } from '../dossier/dossier.component';
import { NotificationsComponent } from '../notifications/notifications.component';
import type { Dossier } from '../interfaces/dossier';
import type { Authority } from '../interfaces/authority';
import type { DossierType } from '../interfaces/dossier-type';

@Component({
	selector: 'app-dossiers-page',
	imports: [
		FormsModule,
		ItTabContainerComponent,
		ItTabItemComponent,
		ItModalComponent,
		ItIconComponent,
		ItInputComponent,
		ItSelectComponent,
		ItBreadcrumbComponent,
		ItBreadcrumbItemComponent,
		ItButtonDirective,
		ItSearchComponent,
		DossierComponent,
		NotificationsComponent,
		RouterLink,
		TranslateModule,
		ItCheckboxComponent,
	],
	templateUrl: './dossiers-page.component.html',
	styleUrl: './dossiers-page.component.scss',
})
export class DossiersPageComponent implements OnInit {
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private iterlexService = inject(IterlexService);

	tabPrefix = 'commission-';
	authority?: Authority;
	body?: Authority;
	authorities: Authority[] = [];
	dossierTypes: DossierType[] = [];
	dossiers: Dossier[] = [];
	dossiersAssigned: Record<string, Dossier[]> = {};
	progressDossierUri = '';
	currentDossier?: Dossier;
	modificationsDestinations: string[] = [];
	showArchivedDossiers = false;
	ngOnInit(): void {
		const authorityId =
			this.activatedRoute.snapshot.queryParamMap.get('authority');
		this.iterlexService.getAuthorities().subscribe((authorities) => {
			this.authorities = authorities;
			for (const authority of authorities) {
				if (authority.id == authorityId) {
					this.body = authority;
					this.authority = authority;
					break;
				} else if (authority.children.length > 0) {
					this.body = authority.children.find(
						(authority) => authority.id == authorityId,
					);
					if (this.body) {
						this.authority = authority;
						break;
					}
				}
			}
			if (this.authority && this.body) {
				if (this.body.id === 'commissione') {
					this.updateDossiersAssigned(this.body.children[0].id);
				} else {
					this.updateDossiers();
				}
				this.iterlexService
					.getDossierTypes(
						new HttpParams()
							.append('authority', this.authority.id)
							.append('body', this.body.id),
					)
					.subscribe(
						(dossierTypes) => (this.dossierTypes = dossierTypes),
					);
			}
		});
	}
	searchDossiers$ = (search?: string | null): Observable<SearchItem[]> => {
		if (!search) {
			return of([]);
		}
		const params = new HttpParams()
			.append('authority', this.authority!.id)
			.append('body', this.body!.id)
			.append('title', search);
		return this.iterlexService.getDossiers(params).pipe(
			map((dossiers) =>
				dossiers.value.map((dossier) => ({
					value: dossier.title,
					icon: 'folder',
					label:
						dossier.number +
						', ' +
						formatDate(dossier.date, 'd/M/yyyy', 'en-US'),
					additionalData: { workUri: dossier.workUri },
				})),
			),
		);
	};
	onSearchSelected(item: SearchItem) {
		this.router.navigate(['/dossier'], {
			queryParams: { uri: item.additionalData.workUri },
		});
	}
	changeTab(tab: ItTabItemComponent) {
		this.updateDossiersAssigned(tab.id.replace(this.tabPrefix, ''));
	}
	updateDossiers() {
		let params = new HttpParams()
			.append('authority', this.authority!.id)
			.append('body', this.body!.id);
		if (this.body!.id == 'assemblea') {
			params = params.append('assignedBodyName', 'assemblea');
		}
		if (this.showArchivedDossiers) {
			params = params.append('state', 'archived');
		} else {
			params = params.append('excludeState', 'archived');
		}
		this.iterlexService
			.getDossiers(params)
			.subscribe((dossiers) => (this.dossiers = dossiers.value));
	}
	updateDossiersAssigned(assignedBodyName: string) {
		this.iterlexService
			.getDossiers(
				new HttpParams()
					.append('authority', this.authority!.id)
					.append('body', this.body!.id)
					.append('assignedBodyName', assignedBodyName),
			)
			.subscribe(
				(dossiers) =>
					(this.dossiersAssigned[assignedBodyName] = dossiers.value),
			);
	}
	getDossierType(dossierTypeId: string) {
		return this.dossierTypes.find((e) => e.id == dossierTypeId);
	}
	onSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.createDossier(
					this.authority!.id,
					this.body!.id,
					form.value.title,
					form.value.number,
					form.value.date,
					form.value.dossierType,
					{
						...(form.value.dossierType == 'leggeModifica' && {
							modificationsDestinations:
								this.modificationsDestinations,
						}),
					},
				)
				.subscribe((dossier) => {
					console.log(dossier);
					this.updateDossiers();
				});
		}
	}
	deleteDossier(workUri: string) {
		this.progressDossierUri = workUri;
		this.iterlexService
			.deleteDossier(this.authority!.id, this.body!.id, workUri)
			.subscribe({
				next: () => {
					this.updateDossiers();
				},
				complete: () => {
					this.progressDossierUri = '';
				},
			});
	}
}
